import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Divider,
  TextField,
  Badge
} from '@mui/material';
import {
  Close,
  Delete,
  Add,
  Remove,
  ShoppingCart,
  ShoppingBag
} from '@mui/icons-material';
import cartService from '../services/cartService';
import uploadService from '../services/uploadService';

const CartDrawer = ({ open, onClose, onCheckout }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });

  useEffect(() => {
    if (open) {
      loadCart();
    }
  }, [open]);

  const loadCart = () => {
    const currentCart = cartService.getCart();
    setCart(currentCart);
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveItem(productId);
      return;
    }

    const updatedCart = cartService.updateQuantity(productId, newQuantity);
    setCart(updatedCart);
  };

  const handleRemoveItem = (productId) => {
    const updatedCart = cartService.removeItem(productId);
    setCart(updatedCart);
  };

  const handleClearCart = () => {
    const emptyCart = cartService.clearCart();
    setCart(emptyCart);
  };

  const handleCheckout = () => {
    if (cart.items.length > 0) {
      onCheckout(cart);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price || 0);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart color="primary" />
            <Typography variant="h6">
              Mi Carrito
            </Typography>
            {cart.items.length > 0 && (
              <Badge badgeContent={cart.items.length} color="primary" />
            )}
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          {cart.items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ShoppingBag sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Tu carrito está vacío
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Agrega productos para crear un pedido
              </Typography>
            </Box>
          ) : (
            <>
              <List>
                {cart.items.map((item, index) => (
                  <React.Fragment key={item.productId}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{ px: 0 }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveItem(item.productId)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={item.image ? uploadService.getImageUrl(item.image) : '/no-image.svg'}
                          alt={item.name}
                          sx={{ width: 60, height: 60, mr: 2 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Box component="div" sx={{ mt: 1 }}>
                            {item.code && (
                              <Typography variant="caption" color="text.secondary" display="block" component="span">
                                Código: {item.code}
                              </Typography>
                            )}
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mt: 0.5 }} component="div">
                              {formatPrice(item.price)}
                            </Typography>
                            
                            {/* Selector de cantidad */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                              >
                                <Remove fontSize="small" />
                              </IconButton>
                              <TextField
                                size="small"
                                value={item.quantity}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 1;
                                  handleUpdateQuantity(item.productId, value);
                                }}
                                inputProps={{
                                  min: 1,
                                  max: item.stock || 999,
                                  style: { textAlign: 'center', width: '50px' }
                                }}
                                type="number"
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}
                                disabled={item.stock && item.quantity >= item.stock}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Box>

                            {/* Subtotal */}
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }} component="div">
                              Subtotal: {formatPrice(item.price * item.quantity)}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                    {index < cart.items.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>

              {/* Botón limpiar carrito */}
              <Button
                fullWidth
                variant="outlined"
                color="error"
                onClick={handleClearCart}
                sx={{ mt: 2 }}
              >
                Vaciar Carrito
              </Button>
            </>
          )}
        </Box>

        {/* Footer */}
        {cart.items.length > 0 && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Items:
                </Typography>
                <Typography variant="body2">
                  {cart.items.reduce((sum, item) => sum + item.quantity, 0)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total:
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                  {formatPrice(cart.total)}
                </Typography>
              </Box>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleCheckout}
              startIcon={<ShoppingCart />}
            >
              Enviar Pedido
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default CartDrawer;

